// =====================================================================================
// INVRT Radar - web console - the HIGH-INTENT organisation delivery client (story H07)
//
// The four frozen routes of the HIGH-INTENT authenticated organisation delivery surface
// (the integration branch's src/high_intent/api/routes/org_delivery.py, contract C-3 and
// C-10):
//
//   POST /v1/org/delivery/preview    aggregate preview, never row payloads
//   GET  /v1/org/delivery/selection  the explicit saved org selection
//   PUT  /v1/org/delivery/selection  save with expected-version CAS (SAVE IS NOT SEND)
//   GET  /v1/org/delivery/readiness  the setup-required truth surface
//
// TRANSPORT. These routes sit OUTSIDE the five signed /v1/radar/* reads: the engine
// authenticates them through its ordinary credential plane, which accepts the caller's
// Supabase bearer JWT (verified against the project's own JWKS) beside the x-api-key
// plane. The browser already holds that JWT, so this client sends it - and NEVER an API
// key, which a browser must never hold. The origin is the HIGH-INTENT API's own, named
// at build time by VITE_HI_API_URL (see ORIGIN LAW below). A production deploy must
// ensure the engine is reachable from the console's origin (its CORS allows the console
// and exposes X-Correlation-Id); the browser test suite mounts a clearly-labelled
// hermetic stand-in for this origin.
//
// PARSE LAW. Every response is parsed fail-closed against the frozen shapes and REFUSED
// when it names another organisation or campaign than the one the user selected - the
// engine resolves the organisation from the principal itself, so the cross-check here is
// the console's own tenant guard: data from another tenant is never rendered, only an
// error. The three preview diagnostics are labelled OVERLAPPING by the contract; a
// response that loses that label is refused, because the UI law forbids summing them.
// =====================================================================================

import { ApiError } from "@/lib/api";

// ORIGIN LAW. Unlike the Edge Function proxy, this client's origin must NOT have a code
// default: the browser-bundle boundary scan (e2e/browser-bundle-boundary.spec.ts, the
// adapter contract's own list) forbids the HIGH-INTENT origin host from entering the
// browser bundle, so the deploy must NAME the endpoint at build time via VITE_HI_API_URL
// and an unset build fails closed (a config_missing state), never guesses an origin.

export function hiDeliveryOrigin(): string | null {
  const raw = import.meta.env.VITE_HI_API_URL;
  if (typeof raw === "string" && raw.trim() !== "") {
    return raw.trim().replace(/\/+$/, "");
  }
  return null;
}

/** The fail-closed error for a build that was never given the endpoint. */
export function hiDeliveryConfigError(): ApiError {
  return new ApiError({
    token: "config_missing",
    message: "This build was not given the HIGH-INTENT delivery endpoint (VITE_HI_API_URL).",
    httpStatus: 0,
    correlationId: null,
  });
}

// ---------------------------------------------------------------------------------------
// The frozen response types
// ---------------------------------------------------------------------------------------

export type DeliveryMode = "all_accepted" | "high_intent" | "tags";

/** The selection policy view (the engine's PolicyView, version 1 only). */
export interface DeliveryPolicy {
  version: number;
  mode: DeliveryMode;
  tags: string[];
}

export interface DeliveryPreviewDiagnostic {
  code: string;
  count: number;
  overlap: boolean;
  mustNotBeSummed: boolean;
}

export interface DeliveryPreview {
  correlationId: string;
  policy: DeliveryPolicy;
  policyRevision: number;
  /** The pool's latest source revision, the engine's decimal string, or null. */
  sourceRevision: string | null;
  geography: { mode: string | null; revision: number | null };
  selection: { accepted: number; highIntent: number; selected: number; selectedByTag: Record<string, number> };
  diagnostics: DeliveryPreviewDiagnostic[];
  evaluatedAt: string;
}

export interface DeliverySelectionState {
  correlationId: string;
  configured: boolean;
  setupRequired: boolean;
  selection: DeliveryPolicy | null;
  revision: number;
  updatedAt: string | null;
  updatedBy: string | null;
}

export type ReadinessSelectionState = "configured" | "setup_required";
export type ReadinessBindingState = "bound" | "missing";
export type ReadinessDestinationState = "enabled" | "disabled" | "missing";
export type ReadinessCoverageState = "active" | "inactive" | "missing";

export interface DeliveryReadiness {
  correlationId: string;
  ready: boolean;
  selection: { state: ReadinessSelectionState; revision: number; mode: string | null };
  binding: { state: ReadinessBindingState };
  destination: { state: ReadinessDestinationState; id: string | null; version: number | null };
  coverage: { state: ReadinessCoverageState; syncState: string | null; geoMode: string | null };
  sourcesReady: boolean;
  sources: Array<{ batchId: string; attested: boolean; revoked: boolean; sourceLabel: string | null }>;
}

export interface DeliverySaveResult {
  correlationId: string;
  selection: DeliveryPolicy;
  revision: number;
  readiness: Omit<DeliveryReadiness, "correlationId" | "sources">;
}

/** The three diagnostics, exactly as the engine's DIAGNOSTIC_CODES orders them. */
export const DELIVERY_DIAGNOSTIC_CODES = [
  "country_not_confirmed_us",
  "source_unattested",
  "explicit_non_us",
] as const;

/** The editable selection document, exactly the engine's policy grammar (_parse_policy):
 * version 1, one of three modes, tags only in tags mode and always non-empty and unique
 * within A/B/C. */
export type DeliverySelection =
  | { version: 1; mode: "all_accepted" }
  | { version: 1; mode: "high_intent" }
  | { version: 1; mode: "tags"; tags: Array<"A" | "B" | "C"> };

// ---------------------------------------------------------------------------------------
// Fail-closed parsing helpers (types.radar.ts's discipline)
// ---------------------------------------------------------------------------------------

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function badContract(message: string): ApiError {
  return new ApiError({
    token: "delivery_read_invalid",
    message,
    httpStatus: 502,
    correlationId: null,
  });
}

const BAD_CONTRACT = "The HIGH-INTENT delivery response did not match the frozen contract.";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function reqString(value: unknown): string {
  if (typeof value !== "string" || value.length === 0) throw badContract(BAD_CONTRACT);
  return value;
}

function nullableString(value: unknown): string | null {
  if (value === null) return null;
  return reqString(value);
}

function reqCount(value: unknown): number {
  if (typeof value !== "number" || !Number.isInteger(value) || value < 0) throw badContract(BAD_CONTRACT);
  return value;
}

function nullableCount(value: unknown): number | null {
  if (value === null) return null;
  return reqCount(value);
}

function reqBool(value: unknown): boolean {
  if (typeof value !== "boolean") throw badContract(BAD_CONTRACT);
  return value;
}

function reqUuid(value: unknown): string {
  const id = reqString(value);
  if (!UUID_PATTERN.test(id)) throw badContract(BAD_CONTRACT);
  return id;
}

function nullableUuid(value: unknown): string | null {
  if (value === null) return null;
  return reqUuid(value);
}

function parsePolicy(value: unknown): DeliveryPolicy {
  if (!isRecord(value)) throw badContract(BAD_CONTRACT);
  const version = reqCount(value.version);
  if (version !== 1) throw badContract(BAD_CONTRACT);
  const mode = value.mode;
  if (mode !== "all_accepted" && mode !== "high_intent" && mode !== "tags") throw badContract(BAD_CONTRACT);
  if (!Array.isArray(value.tags)) throw badContract(BAD_CONTRACT);
  const tags = value.tags.map((tag) => {
    if ((tag !== "A" && tag !== "B" && tag !== "C") || typeof tag !== "string") throw badContract(BAD_CONTRACT);
    return tag;
  });
  if (mode === "tags" && tags.length === 0) throw badContract(BAD_CONTRACT);
  return { version, mode, tags };
}

function parseNullablePolicy(value: unknown): DeliveryPolicy | null {
  if (value === null) return null;
  return parsePolicy(value);
}

/** The parse-time tenant guard: the engine names the organisation it answered for, and
 * the console refuses to render any other tenant's data. The refusal carries its own
 * stable token so the surface can explain it honestly instead of a generic failure. */
function requireTenant(value: Record<string, unknown>, orgId: string, campaignKey: string): void {
  if (!isRecord(value)) throw badContract(BAD_CONTRACT);
  const organization_id = value.organization_id;
  const campaign_key = value.campaign_key;
  if (typeof organization_id !== "string" || organization_id !== orgId) {
    throw new ApiError({
      token: "tenant_mismatch",
      message: "The delivery response names a different organisation. Nothing from another organisation is shown.",
      httpStatus: 502,
      correlationId: typeof value.correlation_id === "string" ? value.correlation_id : null,
    });
  }
  if (typeof campaign_key !== "string" || campaign_key !== campaignKey) {
    throw new ApiError({
      token: "tenant_mismatch",
      message: "The delivery response names a different campaign.",
      httpStatus: 502,
      correlationId: typeof value.correlation_id === "string" ? value.correlation_id : null,
    });
  }
}

function requireCorrelationId(value: Record<string, unknown>): string {
  return reqString(value.correlation_id);
}

export function parseSelectionState(value: unknown, orgId: string, campaignKey: string): DeliverySelectionState {
  if (!isRecord(value)) throw badContract(BAD_CONTRACT);
  requireTenant(value, orgId, campaignKey);
  const configured = reqBool(value.configured);
  const setupRequired = reqBool(value.setup_required);
  if (configured === setupRequired) throw badContract(BAD_CONTRACT);
  const selection = parseNullablePolicy(value.selection);
  if (configured !== (selection !== null)) throw badContract(BAD_CONTRACT);
  return {
    correlationId: requireCorrelationId(value),
    configured,
    setupRequired,
    selection,
    revision: reqCount(value.revision),
    updatedAt: nullableString(value.updated_at),
    updatedBy: nullableString(value.updated_by),
  };
}

const DIAGNOSTIC_CODES_SET = new Set<string>(DELIVERY_DIAGNOSTIC_CODES);

export function parsePreviewResponse(value: unknown, orgId: string, campaignKey: string): DeliveryPreview {
  if (!isRecord(value)) throw badContract(BAD_CONTRACT);
  requireTenant(value, orgId, campaignKey);
  if (!Array.isArray(value.diagnostics) || value.diagnostics.length !== DELIVERY_DIAGNOSTIC_CODES.length) {
    throw badContract(BAD_CONTRACT);
  }
  const seen = new Set<string>();
  const diagnostics = value.diagnostics.map((row) => {
    if (!isRecord(row)) throw badContract(BAD_CONTRACT);
    const code = reqString(row.code);
    if (!DIAGNOSTIC_CODES_SET.has(code) || seen.has(code)) throw badContract(BAD_CONTRACT);
    seen.add(code);
    // The overlap label IS the truth rule: these sets intersect and must never be summed.
    if (reqBool(row.overlap) !== true || reqBool(row.must_not_be_summed) !== true) {
      throw badContract(BAD_CONTRACT);
    }
    reqString(row.note);
    return { code, count: reqCount(row.count), overlap: true, mustNotBeSummed: true };
  });
  if (seen.size !== DELIVERY_DIAGNOSTIC_CODES.length) throw badContract(BAD_CONTRACT);
  if (!isRecord(value.geography)) throw badContract(BAD_CONTRACT);
  if (!isRecord(value.selection)) throw badContract(BAD_CONTRACT);
  const selectedByTagRaw = value.selection.selected_by_tag;
  if (!isRecord(selectedByTagRaw)) throw badContract(BAD_CONTRACT);
  const selectedByTag: Record<string, number> = {};
  for (const tag of ["A", "B", "C"]) {
    const count = reqCount(selectedByTagRaw[tag]);
    if (count < 0) throw badContract(BAD_CONTRACT);
    selectedByTag[tag] = count;
  }
  if (Object.keys(selectedByTagRaw).length !== 3) throw badContract(BAD_CONTRACT);
  const sourceRevisionRaw = value.source_revision;
  if (sourceRevisionRaw !== null && (typeof sourceRevisionRaw !== "string" || !/^\d+$/.test(sourceRevisionRaw))) {
    throw badContract(BAD_CONTRACT);
  }
  return {
    correlationId: requireCorrelationId(value),
    policy: parsePolicy(value.policy),
    policyRevision: reqCount(value.policy_revision),
    sourceRevision: sourceRevisionRaw,
    geography: { mode: nullableString(value.geography.mode), revision: nullableCount(value.geography.revision) },
    selection: {
      accepted: reqCount(value.selection.accepted),
      highIntent: reqCount(value.selection.high_intent),
      selected: reqCount(value.selection.selected),
      selectedByTag,
    },
    diagnostics,
    evaluatedAt: reqString(value.evaluated_at),
  };
}

function parseReadinessSummary(value: Record<string, unknown>): Omit<DeliveryReadiness, "correlationId" | "sources"> {
  if (!isRecord(value.selection) || !isRecord(value.binding) || !isRecord(value.destination) || !isRecord(value.coverage)) {
    throw badContract(BAD_CONTRACT);
  }
  const selectionState = value.selection.state;
  if (selectionState !== "configured" && selectionState !== "setup_required") {
    throw badContract("The readiness selection state was outside the frozen vocabulary.");
  }
  const bindingState = value.binding.state;
  if (bindingState !== "bound" && bindingState !== "missing") {
    throw badContract("The readiness binding state was outside the frozen vocabulary.");
  }
  const destinationState = value.destination.state;
  if (destinationState !== "enabled" && destinationState !== "disabled" && destinationState !== "missing") {
    throw badContract("The readiness destination state was outside the frozen vocabulary.");
  }
  const coverageState = value.coverage.state;
  if (coverageState !== "active" && coverageState !== "inactive" && coverageState !== "missing") {
    throw badContract("The readiness coverage state was outside the frozen vocabulary.");
  }
  return {
    ready: reqBool(value.ready),
    selection: { state: selectionState, revision: reqCount(value.selection.revision), mode: nullableString(value.selection.mode) },
    binding: { state: bindingState },
    destination: {
      state: destinationState,
      id: nullableUuid(value.destination.id),
      version: nullableCount(value.destination.version),
    },
    coverage: {
      state: coverageState,
      syncState: nullableString(value.coverage.sync_state),
      geoMode: nullableString(value.coverage.geo_mode),
    },
    sourcesReady: reqBool(value.sources_ready),
  };
}

// ---------------------------------------------------------------------------------------
// The white-label law (Mark's ruling, 28 Sep 2026): a data-source vendor is NEVER named on
// a client-visible surface, so a readiness source row's raw provider token never survives
// into the parsed shape. The label is read from the token's own declared machine family
// (a list/lead feed, a website-visitor feed) and ANY other token falls to the neutral
// default, so a future vendor can never leak by omission - the mapping is generic over
// every provider token, never a lookup of the two known names.
// ---------------------------------------------------------------------------------------

const LIST_SOURCE_TOKEN = /lead|list/;
const VISITOR_SOURCE_TOKEN = /visit|website/;

function neutralSourceLabel(rawProviderToken: string): string {
  const token = rawProviderToken.toLowerCase();
  if (VISITOR_SOURCE_TOKEN.test(token)) return "website visitor data";
  if (LIST_SOURCE_TOKEN.test(token)) return "list source";
  return "provider";
}

function parseSources(value: unknown): DeliveryReadiness["sources"] {
  if (!Array.isArray(value)) throw badContract(BAD_CONTRACT);
  return value.map((row) => {
    if (!isRecord(row)) throw badContract(BAD_CONTRACT);
    const rawProviderToken = nullableString(row.provider_code);
    return {
      batchId: reqUuid(row.batch_id),
      attested: reqBool(row.attested),
      revoked: reqBool(row.revoked),
      // The raw token is consumed here and goes no further: only the neutral label is kept.
      sourceLabel: rawProviderToken === null ? null : neutralSourceLabel(rawProviderToken),
    };
  });
}

export function parseReadinessResponse(value: unknown, orgId: string, campaignKey: string): DeliveryReadiness {
  if (!isRecord(value)) throw badContract(BAD_CONTRACT);
  requireTenant(value, orgId, campaignKey);
  return {
    correlationId: requireCorrelationId(value),
    ...parseReadinessSummary(value),
    sources: parseSources(value.sources),
  };
}

export function parseSaveResponse(value: unknown, orgId: string, campaignKey: string): DeliverySaveResult {
  if (!isRecord(value)) throw badContract(BAD_CONTRACT);
  requireTenant(value, orgId, campaignKey);
  const revision = reqCount(value.revision);
  if (revision < 1) throw badContract(BAD_CONTRACT);
  if (!isRecord(value.readiness)) throw badContract(BAD_CONTRACT);
  return {
    correlationId: requireCorrelationId(value),
    selection: parsePolicy(value.selection),
    revision,
    readiness: parseReadinessSummary(value.readiness),
  };
}

// ---------------------------------------------------------------------------------------
// The request bodies, exactly the frozen shapes (extra fields are forbidden server-side)
// ---------------------------------------------------------------------------------------

/** The PUT /selection body: campaign_key + the CAS expected_revision + the selection. */
export function selectionBodyFor(campaignKey: string, expectedRevision: number, selection: DeliverySelection): {
  campaign_key: string;
  expected_revision: number;
  selection: DeliverySelection;
} {
  if (!Number.isInteger(expectedRevision) || expectedRevision < 0) {
    throw new ApiError({
      token: "validation_failed",
      message: "The expected revision for this save was not a valid CAS revision.",
      httpStatus: 0,
      correlationId: null,
    });
  }
  return { campaign_key: campaignKey, expected_revision: expectedRevision, selection };
}

// ---------------------------------------------------------------------------------------
// The calls
// ---------------------------------------------------------------------------------------

export interface HiDeliveryContext {
  origin: string;
  /** The caller's Supabase bearer token. The browser never holds an API key. */
  accessToken: string | null;
  fetchImpl?: typeof fetch;
  signal?: AbortSignal;
}

function isRecord2(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

const DELIVERY_REQUEST_DEADLINE_MS = 10_000;

function deliveryRequestError(token: string, correlationId: string | null, httpStatus = 502): ApiError {
  return new ApiError({
    token,
    message: token === "delivery_save_unknown"
      ? "The save outcome is unknown. Check the saved selection before another attempt."
      : "The delivery read could not be completed. Try reading it again.",
    httpStatus,
    correlationId,
  });
}

async function hiCall<T = unknown>(
  ctx: HiDeliveryContext,
  method: "GET" | "POST" | "PUT",
  path: string,
  body?: unknown,
  parse: (value: unknown) => T = (value) => value as T,
): Promise<T> {
  // Preview POST is a read. Only selection PUT can leave an uncertain write.
  const mutation = method === "PUT";
  if (ctx.signal?.aborted) throw ctx.signal.reason;
  const controller = new AbortController();
  let correlationId: string | null = null;
  let rejectInterrupted: (reason: unknown) => void = () => undefined;
  const interrupted = new Promise<never>((_resolve, reject) => { rejectInterrupted = reject; });
  const cancel = () => {
    rejectInterrupted(mutation
      ? deliveryRequestError("delivery_save_unknown", correlationId)
      : ctx.signal?.reason);
    controller.abort(ctx.signal?.reason);
  };
  ctx.signal?.addEventListener("abort", cancel, { once: true });
  const timer = setTimeout(() => {
    rejectInterrupted(deliveryRequestError(mutation ? "delivery_save_unknown" : "delivery_read_timeout", correlationId, 504));
    controller.abort();
  }, DELIVERY_REQUEST_DEADLINE_MS);

  try {
    const request = async (): Promise<T> => {
      let response: Response;
      try {
        response = await (ctx.fetchImpl ?? fetch)(`${ctx.origin}${path}`, {
          method,
          headers: {
            ...(ctx.accessToken === null ? {} : { Authorization: `Bearer ${ctx.accessToken}` }),
            ...(body === undefined ? {} : { "Content-Type": "application/json" }),
            Accept: "application/json",
          },
          ...(body === undefined ? {} : { body: JSON.stringify(body) }),
          signal: controller.signal,
        });
      } catch (cause) {
        if (!mutation && ctx.signal?.aborted) throw ctx.signal.reason;
        if (!mutation && cause instanceof DOMException && cause.name === "AbortError") throw cause;
        throw deliveryRequestError(mutation ? "delivery_save_unknown" : "delivery_read_failed", correlationId);
      }
      correlationId = response.headers.get("X-Correlation-Id");
      if (response.ok) {
        try {
          const value: unknown = await response.json();
          if (isRecord2(value) && typeof value.correlation_id === "string") correlationId = value.correlation_id;
          return parse(value);
        } catch (cause) {
          if (!mutation && ctx.signal?.aborted) throw ctx.signal.reason;
          if (mutation) throw deliveryRequestError("delivery_save_unknown", correlationId, response.status);
          if (cause instanceof ApiError && cause.token !== "delivery_read_invalid") throw cause;
          throw deliveryRequestError("delivery_read_invalid", correlationId);
        }
      }
      let detail: unknown = null;
      try {
        const parsed: unknown = await response.json();
        if (isRecord2(parsed)) detail = parsed.detail ?? null;
      } catch { /* The HTTP status remains authoritative for explicit refusals. */ }
      correlationId = isRecord2(detail) && typeof detail.correlation_id === "string"
        ? detail.correlation_id : correlationId;
      let token = mutation ? "delivery_save_unknown" : "delivery_read_failed";
      if (response.status === 401) token = "unauthenticated";
      else if (response.status === 403) token = "forbidden";
      else if (response.status === 409 && isRecord2(detail) && typeof detail.error === "string") token = detail.error;
      else if (response.status === 422) token = "validation_failed";
      const currentRevision = isRecord2(detail) && "current_revision" in detail ? detail.current_revision : undefined;
      throw new ApiError({
        token,
        message: "The delivery service refused or could not answer this request.",
        httpStatus: response.status,
        correlationId,
        ...(currentRevision === undefined ? {} : { details: { current_revision: currentRevision } }),
      });
    };
    return await Promise.race([request(), interrupted]);
  } finally {
    clearTimeout(timer);
    ctx.signal?.removeEventListener("abort", cancel);
  }
}

function selectionPath(campaignKey: string): string {
  return `/v1/org/delivery/selection?campaign_key=${encodeURIComponent(campaignKey)}`;
}

function readinessPath(campaignKey: string): string {
  return `/v1/org/delivery/readiness?campaign_key=${encodeURIComponent(campaignKey)}`;
}

/** GET /v1/org/delivery/selection: the explicit saved selection or setup-required truth. */
export async function fetchDeliverySelection(
  ctx: HiDeliveryContext,
  campaignKey: string,
  orgId: string,
): Promise<DeliverySelectionState> {
  return hiCall(ctx, "GET", selectionPath(campaignKey), undefined,
    (value) => parseSelectionState(value, orgId, campaignKey));
}

/** GET /v1/org/delivery/readiness: binding, destination, coverage and attestation truth. */
export async function fetchDeliveryReadiness(
  ctx: HiDeliveryContext,
  campaignKey: string,
  orgId: string,
): Promise<DeliveryReadiness> {
  return hiCall(ctx, "GET", readinessPath(campaignKey), undefined,
    (value) => parseReadinessResponse(value, orgId, campaignKey));
}

/** POST /v1/org/delivery/preview: aggregates for the saved selection (selection omitted)
 * or an explicit draft. Aggregates only - never rows. */
export async function fetchDeliveryPreview(
  ctx: HiDeliveryContext,
  campaignKey: string,
  orgId: string,
  draft: DeliverySelection | null,
): Promise<DeliveryPreview> {
  const body = draft === null ? { campaign_key: campaignKey } : { campaign_key: campaignKey, selection: draft };
  return hiCall(ctx, "POST", "/v1/org/delivery/preview", body,
    (value) => parsePreviewResponse(value, orgId, campaignKey));
}

/** PUT /v1/org/delivery/selection: SAVE IS NOT SEND. Stores the selection document under
 * expected-version CAS and returns the new revision with the readiness block. Nothing
 * here contacts a destination. */
export async function saveDeliverySelection(
  ctx: HiDeliveryContext,
  campaignKey: string,
  orgId: string,
  expectedRevision: number,
  selection: DeliverySelection,
): Promise<DeliverySaveResult> {
  return hiCall(ctx, "PUT", selectionPath(campaignKey), selectionBodyFor(campaignKey, expectedRevision, selection),
    (value) => parseSaveResponse(value, orgId, campaignKey));
}

// ---------------------------------------------------------------------------------------
// The draft store (sessionStorage, keyed by organisation + campaign so a refresh keeps
// the user's edits and an organisation switch can never see another tenant's draft)
// ---------------------------------------------------------------------------------------

export interface DeliveryDraft {
  selection: DeliverySelection;
  /** The selection revision the draft was started from (the CAS expected_revision). */
  basedOnRevision: number;
  savedAt: string;
  /** Persist before dispatch so a refresh cannot turn a lost response into a new write. */
  attempt?: {
    status: "unknown" | "retry_allowed" | "matching" | "conflict";
    correlationId: string | null;
  };
}

function draftKey(orgId: string, campaignKey: string): string {
  return `hi-delivery-draft:${orgId}:${campaignKey}`;
}

function isDeliverySelection(value: unknown): value is DeliverySelection {
  if (!isRecord(value) || value.version !== 1) return false;
  if (value.mode === "all_accepted" || value.mode === "high_intent") {
    return Object.keys(value).length === 2;
  }
  if (value.mode === "tags" && Object.keys(value).length === 3 && Array.isArray(value.tags) && value.tags.length > 0) {
    return value.tags.every((tag) => (tag === "A" || tag === "B" || tag === "C") && typeof tag === "string")
      && new Set(value.tags).size === value.tags.length;
  }
  return false;
}

function isDeliveryDraft(value: unknown): value is DeliveryDraft {
  return isRecord(value)
    && isDeliverySelection(value.selection)
    && typeof value.basedOnRevision === "number"
    && Number.isInteger(value.basedOnRevision) && value.basedOnRevision >= 0
    && typeof value.savedAt === "string"
    && (value.attempt === undefined || (isRecord(value.attempt)
      && ["unknown", "retry_allowed", "matching", "conflict"].includes(String(value.attempt.status))
      && (value.attempt.correlationId === null || typeof value.attempt.correlationId === "string")));
}

export function loadDeliveryDraft(orgId: string, campaignKey: string): DeliveryDraft | null {
  try {
    const raw = window.sessionStorage.getItem(draftKey(orgId, campaignKey));
    if (raw === null) return null;
    let parsed: unknown = null;
    try {
      parsed = JSON.parse(raw);
    } catch {
      window.sessionStorage.removeItem(draftKey(orgId, campaignKey));
      return null;
    }
    if (!isDeliveryDraft(parsed)) {
      window.sessionStorage.removeItem(draftKey(orgId, campaignKey));
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function storeDeliveryDraft(orgId: string, campaignKey: string, draft: DeliveryDraft): void {
  try {
    window.sessionStorage.setItem(draftKey(orgId, campaignKey), JSON.stringify(draft));
  } catch {
    // A browser with storage blocked still works; the draft just does not survive a refresh.
  }
}

export function clearDeliveryDraft(orgId: string, campaignKey: string): void {
  try {
    window.sessionStorage.removeItem(draftKey(orgId, campaignKey));
  } catch {
    // Nothing to clear safely.
  }
}
